import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  Plus,
  Trash2,
  Calendar,
  Loader2,
  ClipboardList,
  X,
  Edit2,
} from 'lucide-react';
import { useTaskStore } from '@/store/taskStore';
import { cn } from '@/lib/utils';

export function TasksPage() {
  const {
    tasks,
    completedCount,
    pendingCount,
    showCompleted,
    isLoading,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    toggleTask,
    setShowCompleted,
  } = useTaskStore();

  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    if (editingTask) {
      await updateTask(editingTask, {
        title: formData.title,
        description: formData.description || undefined,
        dueDate: formData.dueDate || null,
      });
    } else {
      await createTask({
        title: formData.title,
        description: formData.description || undefined,
        dueDate: formData.dueDate || undefined,
      });
    }

    setSaving(false);
    setShowModal(false);
    setEditingTask(null);
    setFormData({ title: '', description: '', dueDate: '' });
  };

  const openEditModal = (task: any) => {
    setEditingTask(task.id);
    setFormData({
      title: task.title,
      description: task.description || '',
      dueDate: task.dueDate || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    await deleteTask(id);
  };

  const formatDueDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    const isPast = date < today && !isToday;

    let label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (isToday) label = 'Today';
    if (isTomorrow) label = 'Tomorrow';

    return { label, isPast, isToday };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Tasks & Reminders</h2>
          <p className="text-muted-foreground">Track your tax preparation to-dos</p>
        </div>
        <button
          onClick={() => {
            setEditingTask(null);
            setFormData({ title: '', description: '', dueDate: '' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
        >
          <Plus className="h-4 w-4" />
          Add Task
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
              <Circle className="h-5 w-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{pendingCount}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{completedCount}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowCompleted(true)}
          className={cn(
            'px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth',
            showCompleted
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          )}
        >
          All Tasks
        </button>
        <button
          onClick={() => setShowCompleted(false)}
          className={cn(
            'px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth',
            !showCompleted
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          )}
        >
          Pending Only
        </button>
      </div>

      {/* Task List */}
      <div className="bg-card border border-border rounded-xl">
        {isLoading && tasks.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No tasks yet</p>
            <p className="text-sm">Add a task to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {tasks.map((task) => {
              const dueDateInfo = task.dueDate ? formatDueDate(task.dueDate) : null;
              return (
                <div
                  key={task.id}
                  className={cn(
                    'flex items-start gap-3 p-4 hover:bg-muted/30 transition-smooth group',
                    task.completed && 'opacity-60'
                  )}
                >
                  <button
                    onClick={() => toggleTask(task.id)}
                    className="mt-0.5 flex-shrink-0"
                  >
                    {task.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        'font-medium',
                        task.completed && 'line-through text-muted-foreground'
                      )}
                    >
                      {task.title}
                    </p>
                    {task.description && (
                      <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                        {task.description}
                      </p>
                    )}
                    {dueDateInfo && (
                      <div
                        className={cn(
                          'flex items-center gap-1 mt-1.5 text-xs',
                          dueDateInfo.isPast && !task.completed
                            ? 'text-red-500'
                            : dueDateInfo.isToday
                            ? 'text-yellow-500'
                            : 'text-muted-foreground'
                        )}
                      >
                        <Calendar className="h-3 w-3" />
                        <span>{dueDateInfo.label}</span>
                        {dueDateInfo.isPast && !task.completed && (
                          <span className="font-medium">(Overdue)</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditModal(task)}
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title="Edit task"
                    >
                      <Edit2 className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="p-1.5 rounded hover:bg-destructive/10 transition-colors"
                      title="Delete task"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingTask ? 'Edit Task' : 'Add Task'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingTask(null);
                }}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  placeholder="e.g., Upload W-2 from employer"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Description <span className="text-muted-foreground">(optional)</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg resize-none"
                  rows={3}
                  placeholder="Add any notes or details..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Due Date <span className="text-muted-foreground">(optional)</span>
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData({ ...formData, dueDate: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTask(null);
                  }}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.title.trim()}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  ) : editingTask ? (
                    'Save Changes'
                  ) : (
                    'Add Task'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
